import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Workflow, WorkflowAutomationItem } from "../interfaces";

interface ActivityEvent {
  timestamp: string;
  label: string;
}

interface Props {
  item: WorkflowAutomationItem;
  workflow: Workflow;
}

const LINK_STATUSES = ["LINK", "LINK_UNPUBLISHED"];
const SKIP_STATUSES = ["PREVIEW"];

export default function ActivityLog({ item, workflow }: Props) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const recipientName = (recipientNumber: number, recipientEmail: string) => {
    const config = workflow.signatureConfig;
    const nameMap: Record<number, string> = {
      1: config?.recipientName1 ?? "",
      2: config?.recipientName2 ?? "",
      3: config?.recipientName3 ?? "",
      4: config?.recipientName4 ?? "",
    };
    return nameMap[recipientNumber] || recipientEmail;
  };

  const runnerName = item.initiatingUser?.fullName ?? item.automationUser?.fullName ?? t("Automation");

  const events = useMemo(() => {
    const list: ActivityEvent[] = [];

    // Item created
    if (item.createdAt) {
      list.push({ timestamp: item.createdAt, label: t("Item created by {{name}}", { name: runnerName }) });
    }

    // Outputs created
    item.outputs.forEach((output) => {
      if (output.createdAt) {
        list.push({ timestamp: output.createdAt, label: t("Document created") });
      }
    });

    // Approval requests
    item.approvalRequests.forEach((ar) => {
      if (ar.status === "REQUESTED" && ar.requestedAt) {
        list.push({ timestamp: ar.requestedAt, label: t("Approval requested from {{name}}", { name: ar.approver.fullName }) });
      }
      if (ar.status === "APPROVED" && ar.respondedAt) {
        list.push({ timestamp: ar.respondedAt, label: t("{{name}} approved", { name: ar.approver.fullName }) });
      }
      if (ar.status === "REJECTED" && ar.respondedAt) {
        const label = ar.isChangeRequest
          ? t("{{name}} requested changes", { name: ar.approver.fullName })
          : t("{{name}} rejected", { name: ar.approver.fullName });
        list.push({ timestamp: ar.respondedAt, label });
      }
    });

    // Signature requests
    item.signatureRequests.forEach((sr) => {
      if (SKIP_STATUSES.includes(sr.status)) return;

      const name = recipientName(sr.recipientNumber, sr.recipientEmail);

      if (LINK_STATUSES.includes(sr.status)) {
        if (sr.createdAt) {
          list.push({ timestamp: sr.createdAt, label: t("Signature link generated for {{name}}", { name }) });
        }
      } else if (sr.createdAt) {
        list.push({ timestamp: sr.createdAt, label: t("Signature request sent to {{name}}", { name }) });
      }

      if (sr.accessedAt) {
        list.push({ timestamp: sr.accessedAt, label: t("{{name}} viewed signature request", { name }) });
      }
      if (sr.completedAt) {
        list.push({ timestamp: sr.completedAt, label: t("{{name}} signed", { name }) });
      }
      if (sr.status === "EXPIRED" && sr.updatedAt) {
        list.push({ timestamp: sr.updatedAt, label: t("Signature request expired for {{name}}", { name }) });
      }
      if (sr.status === "CANCELED" && sr.updatedAt) {
        list.push({ timestamp: sr.updatedAt, label: t("Signature request cancelled for {{name}}", { name }) });
      }
    });

    // Output emails
    item.outputEmails.forEach((oe) => {
      if (oe.status === "SENT" && oe.updatedAt) {
        list.push({ timestamp: oe.updatedAt, label: t("Email sent") });
      }
      if (oe.status === "FAILED" && oe.updatedAt) {
        list.push({ timestamp: oe.updatedAt, label: t("Email failed") });
      }
    });

    // Item terminal status
    if (item.status === "COMPLETED" && item.updatedAt) {
      list.push({ timestamp: item.updatedAt, label: t("Item completed") });
    }
    if (item.status === "COMPLETED_WITH_ERROR" && item.updatedAt) {
      list.push({ timestamp: item.updatedAt, label: t("Item completed with error") });
    }
    if (item.status === "CANCELLED" && item.updatedAt) {
      list.push({ timestamp: item.updatedAt, label: t("Item cancelled") });
    }

    // Sort newest first
    list.sort((a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf());

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, workflow, t]);

  const visible = showAll ? events : events.slice(0, 20);

  if (events.length === 0) {
    return <div className="text-xs text-gray-400">{t("No activity yet")}</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {visible.map((event, i) => (
        <div key={i} className="flex flex-col">
          <span className="text-xs text-gray-400">{moment(event.timestamp).format("DD MMMM, h:mma")}</span>
          <span className="text-sm text-gray-600">{event.label}</span>
        </div>
      ))}
      {events.length > 20 && !showAll && (
        <button
          type="button"
          className="text-xs font-semibold text-blue hover:underline self-start"
          onClick={() => setShowAll(true)}
        >
          {t("Show more")}
        </button>
      )}
    </div>
  );
}
